import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, CalendarDays as CalIcon, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";

const STATUS_DOT = {
  pending: "bg-amber-500",
  confirmed: "bg-emerald-500",
  completed: "bg-zinc-500",
  cancelled: "bg-red-500",
};
const STATUS_COLORS = {
  pending: "bg-amber-100 text-amber-900 border-amber-200",
  confirmed: "bg-emerald-100 text-emerald-900 border-emerald-200",
  completed: "bg-slate-100 text-slate-900 border-slate-200",
  cancelled: "bg-red-100 text-red-900 border-red-200",
};
function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function addMonths(d, n) { return new Date(d.getFullYear(), d.getMonth() + n, 1); }
function fmtMonth(d) { return d.toLocaleString(undefined, { month: "long", year: "numeric" }); }
function fmtDate(d) { const y = d.getFullYear(); const m = String(d.getMonth() + 1).padStart(2, "0"); const day = String(d.getDate()).padStart(2, "0"); return `${y}-${m}-${day}`; }
function addDays(d, amount) { const next = new Date(d); next.setDate(next.getDate() + amount); return next; }
function formatTime12h(time) {
  if (!time) return "—";
  const [h, m] = time.split(":");
  const hour = parseInt(h, 10);
  const period = hour >= 12 ? "PM" : "AM";
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${m} ${period}`;
}
export default function BookingsCalendar() {
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const [bookings, setBookings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(fmtDate(new Date()));
  const [selectedDayEvents, setSelectedDayEvents] = useState([]);
  const [showModal, setShowModal] = useState(false);

  useEffect(() => {
    const startDate = fmtDate(startOfMonth(month));
    const endDate = fmtDate(new Date(month.getFullYear(), month.getMonth() + 1, 0));
    api.get("/bookings", { params: { start_date: startDate, end_date: endDate } }).then(({ data }) => setBookings(Array.isArray(data) ? data : data.items || []));
  }, [month]);

  const days = useMemo(() => {
    const first = startOfMonth(month);
    const startWeekday = (first.getDay() + 6) % 7; // Monday-first
    const lastDay = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
    const cells = [];
    for (let i = 0; i < startWeekday; i++) cells.push(null);
    for (let d = 1; d <= lastDay; d++) cells.push(new Date(month.getFullYear(), month.getMonth(), d));
    while (cells.length % 7 !== 0) cells.push(null);
    return cells;
  }, [month]);

  const byDate = useMemo(() => {
    const map = {};
    for (const b of bookings) {
      if (!b.event_date) continue;
      const start = new Date(b.event_date);
      const end = b.end_date ? new Date(b.end_date) : start;
      const days = [];
      let cursor = new Date(start);
      while (cursor <= end) {
        days.push(fmtDate(cursor));
        cursor = addDays(cursor, 1);
      }
      for (const day of days) {
        (map[day] = map[day] || []).push(b);
      }
    }
    return map;
  }, [bookings]);

  const selectedEvents = selectedDate ? (byDate[selectedDate] || []) : [];

  const openDay = (date) => {
    setSelectedDate(date);
    setSelectedDayEvents(byDate[date] || []);
    setShowModal(true);
  };

  return (
    <div className="space-y-6" data-testid="bookings-calendar-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Schedule</div>
          <h1 className="font-serif text-4xl tracking-tighter mt-1 text-[#1A1A1A]">Calendar</h1>
        </div>
        <div className="flex items-center gap-2">
          <Link to="/app/bookings"><Button variant="outline">List view</Button></Link>
          <Link to="/app/bookings/new"><Button className="bg-[#1A1A1A] text-white hover:bg-black rounded-full">+ New booking</Button></Link>
        </div>
      </div>

      <Card className="bg-white border-black/10">
        <CardContent className="p-4 md:p-6">
          <div className="flex items-center justify-between mb-4">
            <Button variant="ghost" size="icon" onClick={() => setMonth(addMonths(month, -1))} data-testid="calendar-prev-btn"><ChevronLeft size={18} /></Button>
            <div className="font-serif text-2xl tracking-tight" data-testid="calendar-month-label">{fmtMonth(month)}</div>
            <Button variant="ghost" size="icon" onClick={() => setMonth(addMonths(month, 1))} data-testid="calendar-next-btn"><ChevronRight size={18} /></Button>
          </div>
          <div className="grid grid-cols-7 gap-px bg-black/5">
            {["Mon","Tue","Wed","Thu","Fri","Sat","Sun"].map((d) => (
              <div key={d} className="bg-white p-2 text-xs uppercase tracking-[0.2em] text-zinc-500 text-center">{d}</div>
            ))}
            {days.map((d, i) => {
              const key = d ? fmtDate(d) : `empty-${i}`;
              const events = d ? (byDate[fmtDate(d)] || []) : [];
              const today = d && fmtDate(d) === fmtDate(new Date());
              return (
                <div key={key} role="button" tabIndex={d ? 0 : -1} onClick={() => d && openDay(fmtDate(d))} onKeyDown={(e) => { if (d && (e.key === "Enter" || e.key === " ")) { e.preventDefault(); openDay(fmtDate(d)); } }} className={`bg-white min-h-[120px] p-2 text-left ${d ? "cursor-pointer hover:bg-zinc-50" : "bg-zinc-50"}`}>
                  {d && (
                    <>
                      <div className={`text-xs mb-1 ${today ? "inline-flex items-center justify-center w-6 h-6 rounded-full bg-[#D85C38] text-white" : "text-zinc-500"}`}>{d.getDate()}</div>
                      <div className="space-y-1">
                        {events.slice(0, 3).map((b) => (
                          <Link key={b.id} to={`/app/bookings/${b.id}`} className="block text-xs px-1.5 py-1 rounded bg-[#F5F4F0] hover:bg-[#EFEDE6] truncate" data-testid={`calendar-event-${b.id}`}>
                            <span className={`inline-block w-1.5 h-1.5 rounded-full mr-1 align-middle ${STATUS_DOT[b.status] || "bg-zinc-400"}`} />
                            <span className="align-middle">{b.client_name} · {b.event_type_name}</span>
                          </Link>
                        ))}
                        {events.length > 3 && (
                          <div className="text-[10px] text-zinc-500">+{events.length - 3} more</div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => setShowModal(false)}>
          <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.25em] text-zinc-500">Day view</div>
                <div className="font-serif text-2xl tracking-tight text-[#1A1A1A]">{selectedDate}</div>
              </div>
              <div className="flex items-center gap-2">
                <Link to={`/app/bookings/new?event_date=${selectedDate}&end_date=${selectedDate}`}>
                  <Button size="sm" className="bg-[#D85C38] hover:bg-[#B84A2A] text-white rounded-full">Add booking</Button>
                </Link>
                <Button variant="ghost" size="icon" onClick={() => setShowModal(false)}><X size={16} /></Button>
              </div>
            </div>
            {selectedDayEvents.length === 0 ? (
              <div className="text-sm text-zinc-500">No bookings for this day.</div>
            ) : (
              <div className="space-y-2 max-h-[60vh] overflow-auto">
                {selectedDayEvents.map((b) => (
                  <Link key={b.id} to={`/app/bookings/${b.id}`} className="flex items-center justify-between rounded-xl border border-black/10 px-3 py-3 text-sm hover:bg-zinc-50">
                    <div>
                      <div className="font-medium">{b.client_name}</div>
                      <div className="text-xs text-zinc-500">{b.event_type_name}</div>
                    </div>
                    <Badge variant="outline" className={`capitalize border ${STATUS_COLORS[b.status] || ""}`}>{b.status}</Badge>
                    <div className="text-xs text-zinc-500">{formatTime12h(b.event_start_time)}</div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
